import React from "react";
import {
  Button,
  RECORD_FIELD_ROWS,
  RecordBlock,
  RecordCard,
  RecordField,
  UNSET,
} from "../../../components/ui";
import { BrandDetails } from "../types/brand.types";
import { brandAddressLine as addressLine } from "../brandAddress";

/**
 * A number with its dialling code. The code stays in the label's grey so the
 * value still reads as just the number — and it is rendered rather than
 * stored, the same way the form draws it beside the field.
 */
const DiallingCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="font-normal text-ink-muted">+91</span> {children}
  </>
);

interface BrandOverviewProps {
  details: BrandDetails;
  /** Opens the form, filled with these same answers. */
  onEdit: () => void;
}

/**
 * The Details tab at rest: the brand as it is stored, and the one control that
 * changes it.
 *
 * The tab used to be the form and nothing else, which made every visit an edit
 * — the commonest one being somebody checking which account they are paid
 * into. A brand that is set up is read back here instead, the way the Settings
 * screen reads a profile back, and for the same three reasons: the common
 * visit needs nothing to be edited, nothing can be changed by a stray
 * keystroke on the way past, and the foot of a phone stays the app's tab bar
 * rather than a Save row for a form nobody opened. See `MyBrandPage`, which
 * decides which of the two is drawn, and `BrandDetailsForm`, which takes the
 * bottom edge while it is open.
 *
 * A brand that is *not* set up never gets here — the page opens straight on
 * the form, because there is nothing to read back and the work is the point.
 *
 * The cards, rows and captions are the profile's own — see `RecordFields` —
 * so the two records read as two pages of one app.
 */
const BrandOverview: React.FC<BrandOverviewProps> = ({ details, onEdit }) => {
  const address = addressLine(details);

  return (
    // A named region rather than a bare box: it is a distinct part of the tab
    // — the brand as stored, beside the documents it produces — and naming it
    // is what lets a screen reader jump to it.
    <section aria-label="Brand details" className="flex flex-col gap-4">
      <RecordCard>
        {/* The header a phone opens a record with: the mark large and centred,
            the name under it, and the one action across the full width where a
            thumb is. From `md` up it is the design's own row — a small tile,
            the name beside it, and the action at the far end. */}
        <div className="flex flex-col items-center gap-3 px-5 py-6 text-center md:flex-row md:flex-wrap md:px-6 md:py-5 md:text-left">
          {/* The logo as stored, and a lettered tile where there is none. Not
              the specimen lockup the documents fall back to: this card says
              what is on the record, and standing somebody else's artwork in
              the place their logo will go would be the one claim a read-back
              screen must not make. */}
          {details.logo ? (
            <img
              src={details.logo.dataUrl || details.logo.url}
              alt=""
              className="h-20 w-20 flex-shrink-0 rounded-md border border-line object-contain p-1 md:h-11 md:w-11"
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-md border border-line bg-[#F8FEFF] text-[28px] font-semibold text-[#00899B] md:h-11 md:w-11 md:text-[16px]"
            >
              {(details.brandName || details.fullName || "?")
                .charAt(0)
                .toUpperCase()}
            </span>
          )}

          <div className="min-w-0 md:mr-auto">
            <h2 className="text-[20px] font-medium text-ink">
              {details.brandName || "Your brand"}
            </h2>
            {/* Who the brand belongs to, which is what the documents sign
                themselves with. Quiet, because the brand name above is what
                this card is called. */}
            <p className="mt-0.5 text-[13px] 3xl:text-[14px] text-ink-muted">
              {details.fullName || UNSET}
            </p>
          </div>

          {/* Full width on a phone, where it is the screen's one action and
              belongs under the thumb. From `md` up it sits at the end of the
              row instead — the same place the profile puts its own. */}
          <Button size="sm" className="w-full md:w-auto" onClick={onEdit}>
            Edit brand
          </Button>
        </div>
      </RecordCard>

      <RecordCard title="Communication details">
        <div className={`${RECORD_FIELD_ROWS} px-5 md:gap-x-10 md:px-6`}>
          <RecordField
            label="Phone:"
            value={
              details.phone ? (
                <DiallingCode>{details.phone}</DiallingCode>
              ) : (
                UNSET
              )
            }
          />
          <RecordField
            label="WhatsApp"
            value={
              details.whatsapp ? (
                <DiallingCode>{details.whatsapp}</DiallingCode>
              ) : (
                UNSET
              )
            }
          />
        </div>
        {/* An email and an address both run long, so both take a block of
            their own rather than the right-hand end of a row. */}
        <div className="border-t border-line">
          <RecordBlock label="Email" value={details.email || UNSET} />
        </div>
        <div className="border-t border-line">
          <RecordBlock label="Address" value={address || UNSET} />
        </div>
      </RecordCard>

      <RecordCard title="Payment details">
        <div className={`${RECORD_FIELD_ROWS} px-5 md:gap-x-10 md:px-6`}>
          <RecordField
            label="Account holder"
            value={details.accountHolder || UNSET}
          />
          {/* Printed in full rather than masked. It is the reader's own
              account on the reader's own screen, and it is on every invoice
              this brand sends out — a screen that hid it would be hiding the
              one thing somebody opens this tab to check. `tabular-nums` so the
              digits can be read off in groups. */}
          <RecordField
            label="Account number"
            value={
              details.accountNumber ? (
                <span className="tabular-nums">{details.accountNumber}</span>
              ) : (
                UNSET
              )
            }
          />
          <RecordField label="IFSC" value={details.ifsc || UNSET} />
        </div>
        <div className="border-t border-line">
          {/* A bank's full name is longer than a row's right-hand end — "The
              Jammu and Kashmir Bank Limited" is a real one. */}
          <RecordBlock label="Bank name" value={details.bankName || UNSET} />
        </div>

        <div
          className={`${RECORD_FIELD_ROWS} border-t border-line px-5 md:gap-x-10 md:px-6`}
        >
          <RecordField
            label="UPI number"
            value={
              details.upiNumber ? (
                <DiallingCode>{details.upiNumber}</DiallingCode>
              ) : (
                UNSET
              )
            }
          />
          <RecordField label="UPI ID" value={details.upiId || UNSET} />
        </div>

        {/* The QR as an image rather than as a filename: it is the one stored
            value that cannot be read as text, and whether the right square is
            on the record is exactly what somebody checks. The uploaded one
            only — the specimen the documents fall back to would be a claim
            about a code nobody can pay. */}
        <div className="border-t border-line px-5 py-3.5 md:px-6 md:py-4">
          <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
            Payment QR
          </div>
          {details.paymentQr ? (
            <img
              src={details.paymentQr.dataUrl || details.paymentQr.url}
              alt="Payment QR code"
              className="mt-2 h-24 w-24 rounded-md border border-line object-contain p-1"
            />
          ) : (
            <div className="mt-1 text-[14px] 3xl:text-[15px] font-medium text-ink">
              {UNSET}
            </div>
          )}
        </div>
      </RecordCard>
    </section>
  );
};

export default BrandOverview;
